import React, { useCallback, useEffect, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import "leaflet/dist/leaflet.css";
import L from "leaflet";

import {
    getConfigMapCenter,
    getConfigTimezone,
    getConfighideTrackButtons,
    getConfigLeafletTileUrl,
} from "../../store/configSlice";
import {
    getMapCenter,
    getIsTrackShown,
    getShownTrackTransportsId,
    getShowPopupFor,
    showPopup,
    hidePopup,
    toggleTrack,
    moveMapTo,
} from "../../store/mapSlice";
import { getFilteredTransports } from "../../store/filterSlice";
import { addControlPlaceholders } from "../../util/leafletUtils";

import { Point } from "./Point";
import { Track } from "./Track";

const MapBody = ({
    nodeId,
    timezone,
    transports,
    toggleTrack,
    shownTrackTransportsId,
    isTrackShown,
    mapCenter,
    showPopupFor,
    showPopup,
    hidePopup,
    moveMapTo,
    hideTrackButtons,
    leafletTileUrl,
}) => {


    const [map, setMap] = useState(null);
    const [mapReady, setMapReady] = useState(false);

    // Используется в Point, чтоб каждый маркер не подключать к стору
    window.toggleHintTrack = useCallback(
        (el) => {
            const id = +el.dataset.id;

            if (!isNaN(id)) {
                toggleTrack(id);
            }
        },
        [toggleTrack]
    );

    useEffect(() => {
        if (!map) {
            const map = L.map(nodeId, {
                zoomAnimation: false,
                zoomControl: true,
                attributionControl: true,
            });

            window.map = map;

            L.tileLayer(leafletTileUrl, {
                attribution:
                    'Map data &copy; <a href="https://openstreetmap.org">OpenStreetMap</a> contributors, <a href="https://creativecommons.org/licenses/by-sa/2.0/">CC-BY-SA</a>',
                maxZoom: 18,
            }).addTo(map);

            map.whenReady(() => {
                setMapReady(true);
            });

            map.on("moveend", () => {
                const { lat, lng } = map.getCenter();

                moveMapTo({ lat, lng });
            });

            map.on("zoomend", () => {
                const zoom = map.getZoom();

                // Добавил null чтоб не прыгало. Лефлету видней куда центрироваться,  после зум сработает moveend
                moveMapTo({ lat: null, lng: null, zoom });
            });

            if (process.env.NODE_ENV === "development") {
                window.L = L;
            }

            setMap(map);

            addControlPlaceholders(map);

            map.zoomControl.setPosition("verticalcenterright");
        }
    }, [nodeId, map, setMap, setMapReady, moveMapTo]);

    useEffect(() => {
        if (map && mapCenter) {
            if (mapReady) {
                const { lat, lng, zoom } = mapCenter;
                const currCenter = map.getCenter();
                const currZoom = map.getZoom();

                if (lat && lng && zoom) {
                    if (
                        currCenter.lat !== lat ||
                        currCenter.lng !== lng ||
                        currZoom !== zoom
                    ) {
                        map.setView({ lat, lng }, zoom);
                    }
                }
            } else {
                const { lat, lng, zoom } = mapCenter;

                map.setView({ lat, lng }, zoom);
            }
        }
    }, [map, mapReady, mapCenter]);


    const getTimeOffset = useCallback(
        (timestampWOMsec) => {
            let timezoneUser;

            if ((new Date().getTimezoneOffset() / -60).toString().includes('-')) {
                timezoneUser = `${(new Date().getTimezoneOffset() / -60)}.0`
            } else {
                timezoneUser = `+${new Date().getTimezoneOffset() / -60}.0`;
            }


            const timestamp = timestampWOMsec * 1000;
            const utc0Timestamp = timestamp - timezoneUser * 60 * 60 * 1000;

            const localTimezone = -new Date().getTimezoneOffset() / 60;
            const localTimestamp =
                utc0Timestamp + localTimezone * 60 * 60 * 1000;

            const secondGap = Math.round((Date.now() - localTimestamp) / 1000);

            if (secondGap < 60) {
                return `${secondGap} сек.`;
            }

            const minuteGap = Math.round(secondGap / 60);

            if (minuteGap < 60) {
                return `${minuteGap} мин.`;
            }

            const hourGap = Math.round(minuteGap / 60);

            if (hourGap < 24) {
                return `${hourGap} час.`;
            }

            const dayGap = Math.round(hourGap / 24);

            return `${dayGap} дн.`;
        },
        [timezone]
    );

    if (!map) {
        return null;
    }

    return (
        <>
            {transports.filter((item) => !item.atGarage).map((transport) => {
                const isThisTrackShowing =
                    transport.id === shownTrackTransportsId && !isTrackShown;
                const isThisTrackShown =
                    transport.id === shownTrackTransportsId && isTrackShown;
                const isPopupShown = transport.id === showPopupFor;

                return (
                    <Point
                        key={transport.id}
                        map={map}
                        transport={transport}
                        getTimeOffset={getTimeOffset}
                        isTrackShowing={isThisTrackShowing}
                        isTrackShown={isThisTrackShown}
                        isPopupShown={isPopupShown}
                        renderTime={isPopupShown ? Date.now() : undefined}
                        showPopup={showPopup}
                        hidePopup={hidePopup}
                        hideTrackButtons={hideTrackButtons}
                    />
                );
            })}
            <Track map={map} />
        </>
    );
};

MapBody.propTypes = {
    nodeId: PropTypes.string.isRequired,
    timezone: PropTypes.string.isRequired,
    transports: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.number.isRequired,
            number: PropTypes.string.isRequired,
            time: PropTypes.number.isRequired,
            lng: PropTypes.number.isRequired,
            lat: PropTypes.number.isRequired,
            speed: PropTypes.number.isRequired,
            company: PropTypes.string.isRequired,
            iconUrl: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired,
        })
    ).isRequired,
    shownTrackTransportsId: PropTypes.number,
    isTrackShown: PropTypes.bool.isRequired,
    mapCenter: PropTypes.shape({
        lat: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.instanceOf(null),
        ]),
        lng: PropTypes.oneOfType([
            PropTypes.number,
            PropTypes.instanceOf(null),
        ]),
        zoom: PropTypes.number.isRequired,
    }),
    showPopupFor: PropTypes.number,
    showPopup: PropTypes.func.isRequired,
    hidePopup: PropTypes.func.isRequired,
    toggleTrack: PropTypes.func.isRequired,
    moveMapTo: PropTypes.func.isRequired,
    hideTrackButtons: PropTypes.bool,
    leafletTileUrl: PropTypes.string.isRequired,
};

const Map = connect(
    (state) => ({
        // Проверяем что конфиг есть, но данные центра берем из другого месте
        mapCenter: getConfigMapCenter(state) ? getMapCenter(state) : null,
        timezone: getConfigTimezone(state),
        transports: getFilteredTransports(state),
        shownTrackTransportsId: getShownTrackTransportsId(state),
        isTrackShown: getIsTrackShown(state),
        showPopupFor: getShowPopupFor(state),
        hideTrackButtons: getConfighideTrackButtons(state),
        leafletTileUrl: getConfigLeafletTileUrl(state),
    }),
    {
        toggleTrack,
        showPopup,
        hidePopup,
        moveMapTo,
    }
)(MapBody);

Map.propTypes = {
    nodeId: PropTypes.string.isRequired,
};

export { Map };
